// GlossaryPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const GlossaryPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Resources', path: '/resources' },
    { name: 'Glossary', path: '/resources/glossary' },
  ];

  const glossaryTerms = [
    {
      term: 'Bale',
      definition: 'A large, compressed bundle of scrap material, typically metals or paper, used for efficient storage and transport.',
    },
    {
      term: 'Broker',
      definition: 'An individual or company that acts as an intermediary between buyers and sellers of scrap materials, facilitating transactions.',
    },
    {
      term: 'Circular Economy',
      definition: 'An economic system aimed at eliminating waste and the continual use of resources. It involves reusing, repairing, refurbishing, and recycling existing materials and products.',
    },
    {
      term: 'Contamination',
      definition: 'Undesirable foreign materials mixed with a primary scrap material, which can reduce its purity and value, requiring additional processing.',
    },
    {
      term: 'Downcycling',
      definition: 'The recycling of waste materials into new materials of lesser quality and reduced functionality.',
    },
    {
      term: 'EAF (Electric Arc Furnace)',
      definition: 'A furnace that heats charged material by means of an electric arc. Primarily used for melting scrap steel to produce new steel.',
    },
    {
      term: 'Ferrous Metals',
      definition: 'Metals that contain iron as their primary component, such as steel and cast iron. They are magnetic and prone to rust.',
    },
    {
      term: 'Non-Ferrous Metals',
      definition: 'Metals that do not contain iron in significant amounts, such as aluminum, copper, brass, and zinc. They are typically corrosion-resistant and non-magnetic.',
    },
    {
      term: 'Shredded Scrap',
      definition: 'Scrap metal that has been processed through a shredder, breaking it into smaller, more manageable pieces, often from end-of-life vehicles or appliances.',
    },
    {
      term: 'Smelting',
      definition: 'A process of applying heat to ore in order to extract a base metal. It is a form of extractive metallurgy.',
    },
    {
      term: 'Upcycling',
      definition: 'The process of transforming waste materials or unwanted products into new materials or products of better quality or for better environmental value.',
    },
    {
      term: 'Virgin Material',
      definition: 'Raw material that has not been previously used or processed, directly extracted from natural resources.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Glossary of Terms
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Navigate the world of scrap and recycling with confidence. Our comprehensive glossary provides clear definitions for key terms, industry jargon, and material classifications.
        </Typography>

        <Box sx={{ mt: 6 }}>
          <List>
            {glossaryTerms.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="h6" fontWeight={600} sx={{ color: 'primary.dark' }}>
                        {item.term}
                      </Typography>
                    }
                    secondary={
                      <Typography component="span" variant="body1" color="text.secondary">
                        {item.definition}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < glossaryTerms.length - 1 && <Divider component="li" sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Container>
    </Box>
  );
};

export default GlossaryPage;
